import React, { useState, useRef, useEffect, useCallback } from 'react';
import Modal from './Modal';
import { ModalProps, ModalSize } from './Modal.types';
import { Icon, IconName, Button, ButtonType, ButtonSize } from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { DocumentFileIcon } from '../icons';
import { PetParentInfo, PetInfo, ValidationError } from '../../types/petParent';
import { usePetParentReportActions } from '../../hooks/usePetParentReports';
import { analytics } from '../../services/analytics';

// Define a type for the parsed data from the spreadsheet
export interface ParsedUploadData {
  petParentInfo: PetParentInfo;
  petInfos: PetInfo[];
  validationErrors?: ValidationError[];
}

export interface SpreadsheetUploadModalProps extends Omit<ModalProps, 'children'> {
  /**
   * Report title
   */
  reportTitle?: string;
  /**
   * Report timeframe
   */
  reportTimeframe?: string;
  /**
   * Function to call when the continue button is clicked
   * Should return a promise that resolves when upload/processing is complete
   */
  onContinue: (file: File | null, onProgress?: (progress: number) => void) => Promise<void>;
  /**
   * Function to download template with format selection
   */
  onDownloadTemplate: (format: 'xlsx' | 'csv') => void;
  /**
   * Current upload progress (0-100)
   */
  uploadProgress?: number;
  /**
   * Whether the file is currently being processed after upload
   */
  isProcessing?: boolean;
}

/**
 * Modal for uploading spreadsheets with pet parent data
 */
const SpreadsheetUploadModal: React.FC<SpreadsheetUploadModalProps> = ({
  title,
  eyebrow = 'Report Title',
  secondaryEyebrow,
  reportTitle,
  reportTimeframe,
  isOpen,
  onClose,
  onContinue,
  onDownloadTemplate,
  uploadProgress = 0,
  isProcessing = false,
  size = ModalSize.Medium,
  showCloseButton = true,
  className = '',
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [internalProgress, setInternalProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { downloadInstructionsTemplate } = usePetParentReportActions();

  // Use external progress if provided, otherwise use internal progress
  const currentProgress = uploadProgress || internalProgress;

  // Handle the file upload
  const handleFileUpload = useCallback(async () => {
    console.log('handleFileUpload called with file:', selectedFile);
    if (selectedFile) {
      try {
        setIsUploading(true);
        setInternalProgress(0);
        setError(null); // Clear any previous errors

        // Call the onContinue prop with progress callback
        await onContinue(selectedFile, (progress: number) => {
          setInternalProgress(progress);
        });

        // Upload completed successfully
        setIsUploading(false);
        setInternalProgress(100);
      } catch (error) {
        console.error('Upload failed:', error);
        setIsUploading(false);
        setInternalProgress(0);

        // Display the error message
        if (error instanceof Error) {
          setError(error.message);
        } else if (typeof error === 'string') {
          setError(error);
        } else if (error && typeof error === 'object' && 'message' in error) {
          setError(error.message as string);
        } else {
          setError(t(TranslationKeyEnum.Error) ?? 'Upload failed');
        }
      }
    } else {
      console.error('Tried to upload with no file selected');
    }
  }, [onContinue, selectedFile, t]);

  // Reset state when the modal is closed or opened
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setIsUploading(false);
      setInternalProgress(0);
      setError(null);
    }
  }, [isOpen]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      validateAndSetFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file: File) => {
    // Clear any previous errors when selecting a new file
    setError(null);

    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidType = fileExtension === 'xlsx' || fileExtension === 'csv';

    // Check file size (10MB = 10,485,760 bytes)
    const isValidSize = file.size <= 10485760;

    if (isValidType && isValidSize) {
      setSelectedFile(file);
    } else {
      // Handle invalid file
      setError(
        !isValidType ? t(TranslationKeyEnum.InvalidFileType) : t(TranslationKeyEnum.FileTooLarge)
      );
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleContinue = async () => {
    // If we have a file and we're not already uploading, start the upload
    if (selectedFile && !isUploading && !isProcessing) {
      await handleFileUpload();
    } else if (!selectedFile) {
      console.error('No file selected for manual continue');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + 'B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
    return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
  };

  const getTimeRemaining = (): string => {
    if (isProcessing) {
      return `${t(TranslationKeyEnum.Processing)}...`;
    }
    return t(TranslationKeyEnum.SecondsLeft, (1.5 - (currentProgress / 100) * 1.5).toFixed(1));
  };

  const renderUploadAnimation = () => {
    return (
      <div className="flex flex-col items-center w-full">
        <div className="flex flex-col items-center gap-4 w-full mb-4">
          {/* File info */}
          <div className="flex flex-col items-center gap-4 w-full">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 bg-[#F2F2F2] rounded-full flex items-center justify-center shadow-md">
                <DocumentFileIcon size={32} color="#0054A4" />
              </div>
              <span className="font-montserrat font-medium text-base text-black/90 text-center max-w-[382px] break-words">
                {selectedFile?.name} ({formatFileSize(selectedFile?.size ?? 0)})
              </span>
            </div>
            <span className="font-montserrat font-medium text-sm text-black/90 text-center">
              {formatFileSize(selectedFile?.size ?? 0)} | {getTimeRemaining()}
            </span>
          </div>

          {/* Progress bar or processing indicator */}
          {isProcessing ? (
            <div className="w-full flex items-center justify-center">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-[#0054A4] border-t-transparent rounded-full animate-spin"></div>
                <span className="font-montserrat font-medium text-sm text-[#0054A4]">
                  {`${t(TranslationKeyEnum.ProcessingFile)}...`}
                </span>
              </div>
            </div>
          ) : (
            <div className="w-full flex items-center">
              <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#2A73B8] via-[#0054A4] to-[#003672] rounded-full"
                  style={{ width: `${currentProgress}%` }}
                ></div>
              </div>
              <div className="ml-2 text-black/95 font-montserrat">{currentProgress}%</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const downloadInstructions = async () => {
    analytics.ctaClick('instructions', 'pet parent', reportTimeframe ?? '');
    await downloadInstructionsTemplate();
  };

  return (
    <Modal
      title={title ?? t(TranslationKeyEnum.UploadASpreadsheet)}
      eyebrow={reportTitle ?? eyebrow}
      secondaryEyebrow={secondaryEyebrow ?? reportTimeframe}
      isOpen={isOpen}
      onClose={(isUploading ?? isProcessing) ? () => {} : onClose}
      size={size}
      showCloseButton={showCloseButton && !isUploading && !isProcessing}
      className={className}
    >
      <div className="flex flex-col w-full">
        {/* Download template with format options */}
        <div className="flex justify-end items-center mb-8">
          <div className="relative">
            <div className="flex gap-2">
              <Button
                label={`${t(TranslationKeyEnum.Instructions)}`}
                type={ButtonType.Secondary}
                size={ButtonSize.Small}
                onClick={() => downloadInstructions()}
                icon={IconName.Download}
                disabled={isUploading ?? isProcessing}
              />
              <Button
                label={`${t(TranslationKeyEnum.NewTemplate)} (.XLSX)`}
                type={ButtonType.Secondary}
                size={ButtonSize.Small}
                onClick={() => onDownloadTemplate('xlsx')}
                icon={IconName.Download}
                disabled={isUploading ?? isProcessing}
              />
              <Button
                label={`${t(TranslationKeyEnum.NewTemplate)} (.CSV)`}
                type={ButtonType.Secondary}
                size={ButtonSize.Small}
                onClick={() => onDownloadTemplate('csv')}
                icon={IconName.Download}
                disabled={isUploading ?? isProcessing}
              />
            </div>
          </div>
        </div>

        {/* Error message display */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md text-red-700 font-montserrat">
            <div className="flex items-center">
              <Icon
                icon={IconName.Error}
                size={24}
                color="#DC2626"
                className="mr-2 flex-shrink-0"
              />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* File upload area */}
        {!isUploading && !isProcessing ? (
          <div
            className="flex flex-col justify-center items-center w-full h-[310px] bg-[#F8F8F8] border border-dashed border-black/60 rounded-2xl cursor-pointer"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleClickUpload}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".xlsx,.csv"
              onChange={handleFileChange}
            />

            <div className="flex flex-col items-center gap-3">
              <div className="mb-2">
                <Icon icon={IconName.Upload} size={48} color="#0054A4" />
              </div>

              <span className="font-montserrat font-semibold text-xl text-[#0054A4] text-center">
                {selectedFile && !isUploading && !isProcessing
                  ? selectedFile.name
                  : t(TranslationKeyEnum.DragFileHereOrClickToUpload)}
              </span>

              <span className="font-montserrat font-medium text-sm text-[#7B7D81] text-center mt-1">
                {t(TranslationKeyEnum.AcceptableFileTypes)}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center w-full h-[310px] bg-[#F8F8F8] rounded-2xl px-20">
            {renderUploadAnimation()}
          </div>
        )}

        {/* Footer with continue button */}
        <div className="flex justify-end w-full mt-8">
          <Button
            label={t(TranslationKeyEnum.Continue)}
            type={ButtonType.Primary}
            size={ButtonSize.Medium}
            onClick={handleContinue}
            disabled={isUploading || isProcessing || !selectedFile}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SpreadsheetUploadModal;
